import { getCookie, setCookie } from "./cookies";
import { getExpirationTime } from "./utils";

export type LocationData = {
  country: string | null;
  state: string | null;
  city: string | null;
  zip: string | null;
  latitude: number | null;
  longitude: number | null;
};

const LOCATION_KEY = "dmp_location";
const LOCATION_TTL_HOURS = 8;
const FALLBACK_REQUEST_TIMEOUT_MS = 5000;
const GEO_WORKER_URL = "https://geo.kiwicredito.com/";

export type SetLocationVisitorOptions = {
  cookieDomain?: string;
};

export function getLocationData(): LocationData | null {
  const stored = readStoredLocation();
  return stored ?? null;
}

export async function setLocationVisitor(
  options: SetLocationVisitorOptions = {}
): Promise<void> {
  if (getLocationData()) return;
  const { cookieDomain } = options;
  try {
    const locationInfo = (await fetchWorkerLocation()) ?? (await fetchFallbackLocation());
    // ponytail: si ambas fuentes fallan no cacheamos nada — el próximo page view reintenta
    if (!locationInfo) return;
    storeLocationData(locationInfo, cookieDomain);
  } catch (error) {
    console.error("Error setting visitor location", error);
  }
}

const storeLocationData = (
  location: LocationData,
  cookieDomain?: string
): void => {
  const serialized = JSON.stringify(location);
  const expires = getExpirationTime(LOCATION_TTL_HOURS);
  const domain = cookieDomain ?? resolveCookieDomain();

  setCookie(LOCATION_KEY, serialized, {
    expires,
    secure: true,
    ...(domain ? { domain } : {}),
  });

  // ponytail: el cookie es la única fuente — el mirror en localStorage no tenía TTL
  // y dejaba la location (incluido "Unknown") cacheada para siempre. Limpiamos el residuo.
  if (isBrowserEnvironment()) {
    window.localStorage.removeItem(LOCATION_KEY);
  }
};

const readStoredLocation = (): LocationData | null => {
  const raw = readStoredValue();
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    return {
      country: coerceString(parsed.country),
      state: coerceString(parsed.state),
      city: coerceString(parsed.city),
      zip: coerceString(parsed.zip),
      latitude: coerceNumber(parsed.latitude),
      longitude: coerceNumber(parsed.longitude),
    };
  } catch (error) {
    console.warn("Failed to parse stored location data", error);
    return null;
  }
};

const readStoredValue = (): string | null => getCookie(LOCATION_KEY);

const coerceString = (value: unknown): string | null => {
  if (typeof value === "string") {
    return value;
  }

  return null;
};

const coerceNumber = (value: unknown): number | null => {
  // Number(null) === 0 y Number("") === 0: sin este guard un lat/long ausente se lee como 0,0
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const resolveCookieDomain = (): string | undefined => {
  if (!isBrowserEnvironment()) {
    return undefined;
  }

  return window.location.hostname;
};

const fetchWorkerLocation = async (): Promise<LocationData | null> => {
  if (!isBrowserEnvironment()) return null;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), FALLBACK_REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(GEO_WORKER_URL, { signal: controller.signal });
    if (!response.ok) {
      console.warn("Geo worker request failed", response.statusText);
      return null;
    }
    const data = (await response.json()) as Record<string, unknown>;
    const country = coerceString(data.country);
    // ponytail: sin country (request.cf vacío) no cacheamos "Unknown" 8h, caemos a ipapi
    if (!country) return null;
    return {
      country,
      state: coerceString(data.state),
      city: coerceString(data.city),
      zip: coerceString(data.zip),
      latitude: coerceNumber(data.latitude),
      longitude: coerceNumber(data.longitude),
    };
  } catch (error) {
    if ((error as Error).name === "AbortError") console.warn("Geo worker request timed out");
    else console.error("Error fetching geo worker location", error);
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
};

const fetchFallbackLocation = async (): Promise<LocationData | null> => {
  if (!isBrowserEnvironment()) {
    return null;
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    FALLBACK_REQUEST_TIMEOUT_MS
  );

  try {
    const response = await fetch("https://ipapi.co/json/", {
      signal: controller.signal,
    });

    if (!response.ok) {
      console.warn("Failed to fetch IP information", response.statusText);
      return null;
    }

    const data = (await response.json()) as Record<string, unknown>;

    return {
      country: coerceString(data.country_name) ?? "Unknown",
      state: coerceString(data.region),
      city: coerceString(data.city),
      zip: coerceString(data.postal),
      latitude: coerceNumber(data.latitude),
      longitude: coerceNumber(data.longitude),
    };
  } catch (error) {
    if ((error as Error).name === "AbortError") {
      console.warn("Fallback IP request timed out");
    } else {
      console.error("Error fetching fallback IP information", error);
    }
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
};

const isBrowserEnvironment = (): boolean =>
  typeof window !== "undefined" && typeof document !== "undefined";
